import { useEffect, useMemo, useState } from "react";
import type { MultiSortOption } from "../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { csvEscape } from "../../utils/csv";
import { multiSortComparator, type SortingState } from "../../utils/multiColumnSorting";
import { parseCsvPreview } from "./utils";

// Roster-resolution columns the transform emits right before the trailing "Attempts" column.
export const EMPLOYEE_MATCH_COLUMN = "Employee Match";
export const ORIGINAL_NAME_COLUMN = "Original Name";

// Canonical columns the transform pipeline outputs for each file type. The shift names are
// `first_name`/`last_name`, not a single "Name" — see `merge_results` in transform_csv.py.
const SHIFT_COLUMNS = [
	"first_name",
	"last_name",
	"Start Date",
	"In Time",
	"Out Time",
	"Location",
	"PTO",
	EMPLOYEE_MATCH_COLUMN,
	ORIGINAL_NAME_COLUMN,
	"Attempts",
] as const;
const TEMPLATE_COLUMNS = [
	"first_name",
	"last_name",
	"week",
	"weekday",
	"start time",
	"end time",
	"location",
	EMPLOYEE_MATCH_COLUMN,
	ORIGINAL_NAME_COLUMN,
	"Attempts",
] as const;

type ShiftRow = Record<(typeof SHIFT_COLUMNS)[number], string>;
type TemplateRow = Record<(typeof TEMPLATE_COLUMNS)[number], string>;

// A real row only has the columns of one file type, so the other half of the intersection is absent — hence Partial.
export type Row = Partial<ShiftRow & TemplateRow>;

type KnownSortColumn = keyof Row;

// Pre-applied sort on these columns (must be a subset of the file type's columns above).
// The roster-resolution columns are APPENDED on purpose: `multiSortComparator` applies these in array
// order as successive tiebreakers, so putting them first would regroup every existing preview.
const SHIFT_DEFAULT_SORT_COLUMNS: Array<keyof ShiftRow> = [
	"first_name",
	"last_name",
	"Start Date",
	"In Time",
	"Out Time",
	"Location",
	"PTO",
	"Attempts",
	EMPLOYEE_MATCH_COLUMN,
	ORIGINAL_NAME_COLUMN,
];
const TEMPLATE_DEFAULT_SORT_COLUMNS: Array<keyof TemplateRow> = [
	"first_name",
	"last_name",
	"week",
	"weekday",
	"start time",
	"end time",
	"location",
	"Attempts",
	EMPLOYEE_MATCH_COLUMN,
	ORIGINAL_NAME_COLUMN,
];

// Forces calendar order (Sun → Sat) for weekday strings; covers full names + 3-letter abbrevs in any case.
const WEEKDAY_ORDER: Map<string, number> = (() => {
	const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
	const m = new Map<string, number>();
	days.forEach((day, idx) => {
		const variants = [day, day.toLowerCase(), day.toUpperCase(), day.slice(0, 3), day.slice(0, 3).toLowerCase(), day.slice(0, 3).toUpperCase()];
		for (const v of variants) m.set(v, idx);
	});
	return m;
})();

export type EmployeeMatchState = "matched" | "merged" | "ambiguous" | "notFound";

export type EmployeeMatchCounts = Record<EmployeeMatchState, number>;

/**
 * The transform emits exactly `matched (<score>)`, `merged (<score>)`, `ambiguous` or `not found` in the
 * Employee Match column. Anything else (an empty cell, a legacy CSV) stays unclassified so it renders as
 * plain text. `merged` is tested before `matched` so a merge verdict can never be swallowed by the
 * benign-looking prefix.
 */
export function classifyEmployeeMatch(value: string): EmployeeMatchState | null {
	const normalized = value.trim().toLowerCase();
	if (normalized === "ambiguous") return "ambiguous";
	if (normalized === "not found") return "notFound";
	if (normalized.startsWith("merged")) return "merged";
	if (normalized.startsWith("matched")) return "matched";
	return null;
}

export function countEmployeeMatchStates(rows: string[][], columnIndex: number): EmployeeMatchCounts {
	const counts: EmployeeMatchCounts = { matched: 0, merged: 0, ambiguous: 0, notFound: 0 };
	if (columnIndex < 0) return counts;
	for (const row of rows) {
		const state = classifyEmployeeMatch(row[columnIndex] ?? "");
		if (state) counts[state] += 1;
	}
	return counts;
}

function pickDefaultSortColumns(headers: string[], fileType: "shifts" | "template" | undefined): KnownSortColumn[] {
	if (!fileType) return [];
	const targets: KnownSortColumn[] = fileType === "template" ? TEMPLATE_DEFAULT_SORT_COLUMNS : SHIFT_DEFAULT_SORT_COLUMNS;
	return targets.filter((t) => headers.includes(t));
}

function pickKnownColumns(headers: string[], fileType: "shifts" | "template" | undefined): KnownSortColumn[] {
	if (!fileType) return [];
	const all: readonly KnownSortColumn[] = fileType === "template" ? TEMPLATE_COLUMNS : SHIFT_COLUMNS;
	return all.filter((c) => headers.includes(c));
}

export function useAttemptsFilter(transformedData: ArrayBuffer | null, fileType?: "shifts" | "template") {
	const preview = useMemo(() => (transformedData ? parseCsvPreview(transformedData) : null), [transformedData]);
	const attemptsColumnIndex = preview?.headers.findIndex((h) => h === "Attempts") ?? -1;
	const hasAttemptsColumn = attemptsColumnIndex >= 0;

	const [attemptsFilter, setAttemptsFilter] = useState<string[]>([]);

	// Default to consensus-only when Attempts column appears
	useEffect(() => {
		if (hasAttemptsColumn) setAttemptsFilter(["consensus"]);
	}, [hasAttemptsColumn]);

	const defaultSortState = useMemo<SortingState<Row>[]>(() => {
		if (!preview) return [];
		return pickDefaultSortColumns(preview.headers, fileType).map((col) => ({
			sortColumns: [col],
			sortOrder: "asc" as const,
			label: col,
		}));
	}, [preview, fileType]);

	const sortingOptions = useMemo<MultiSortOption<Row>[]>(() => {
		if (!preview) return [];
		return pickKnownColumns(preview.headers, fileType).map((col) => ({ label: col, sortColumns: [col] }));
	}, [preview, fileType]);

	// Reset sort to defaults whenever data/fileType changes (no localStorage persistence by design).
	const [sortState, setSortState] = useState<SortingState<Row>[]>(defaultSortState);
	useEffect(() => {
		setSortState(defaultSortState);
	}, [defaultSortState]);

	// Single source of truth: attempts-filtered + sorted. Drives both preview rendering and the uploaded/downloaded CSV.
	const filteredRows = useMemo<string[][]>(() => {
		if (!preview) return [];
		const rows = hasAttemptsColumn
			? preview.rows.filter((row) => {
					const val = row[attemptsColumnIndex] || "";
					return attemptsFilter.some((f) => {
						if (f === "consensus") return val === "all";
						if (val === "all") return false;
						return val.split("+").some((part) => part.trim() === f);
					});
				})
			: preview.rows;

		if (sortState.length === 0) return rows;
		const headers = preview.headers;
		const cmp = multiSortComparator(sortState, WEEKDAY_ORDER);
		// Only populate columns we recognize for the file type; the comparator only reads sortable columns.
		const knownColIndexes = pickKnownColumns(headers, fileType).map((col) => [col, headers.indexOf(col)] as const);
		return [...rows]
			.map((row) => {
				const obj: Row = {};
				knownColIndexes.forEach(([col, idx]) => {
					obj[col] = row[idx] ?? "";
				});
				return { row, obj };
			})
			.sort((a, b) => cmp(a.obj, b.obj))
			.map((d) => d.row);
	}, [preview, hasAttemptsColumn, attemptsColumnIndex, attemptsFilter, sortState, fileType]);

	const employeeMatchColumnIndex = preview?.headers.findIndex((h) => h === EMPLOYEE_MATCH_COLUMN) ?? -1;
	const hasEmployeeMatchColumn = employeeMatchColumnIndex >= 0;
	// Counted over the filtered rows so the summary always describes what's actually on screen.
	const employeeMatchCounts = useMemo(() => countEmployeeMatchStates(filteredRows, employeeMatchColumnIndex), [filteredRows, employeeMatchColumnIndex]);

	function getCsvBlob(): Blob {
		if (!preview) return new Blob([transformedData ?? new ArrayBuffer(0)], { type: "text/csv" });
		const csv = [preview.headers.map(csvEscape).join(","), ...filteredRows.map((row) => row.map(csvEscape).join(","))].join("\n");
		return new Blob([csv], { type: "text/csv" });
	}

	return {
		preview,
		filteredRows,
		hasAttemptsColumn,
		attemptsFilter,
		setAttemptsFilter,
		getCsvBlob,
		sortState,
		setSortState,
		sortingOptions,
		defaultSortState,
		hasEmployeeMatchColumn,
		employeeMatchCounts,
	};
}
